// Utility functions for fetching itch.io game data

/**
 * Extract game information from itch.io URL
 * @param {string} url - The itch.io game URL
 * @returns {object} - Game information including embed URL and thumbnail
 */
export const parseItchioUrl = (url) => {
  if (!url || !url.includes('itch.io')) {
    return null;
  }

  try {
    // Extract username and game name from URL
    // Format: https://username.itch.io/game-name
    const urlObj = new URL(url);
    const hostname = urlObj.hostname;
    const pathname = urlObj.pathname;
    
    // Extract username from subdomain
    const username = hostname.split('.')[0];
    
    // Extract game name from path
    const gameName = pathname.replace('/', '');
    
    if (!username || !gameName || hostname === 'itch.io') {
      return null;
    }

    return {
      username,
      gameName,
      originalUrl: url,
      embedUrl: `https://itch.io/embed/${username}/${gameName}`,
      thumbnailUrl: `https://img.itch.zone/aW1nLzE=/original.png`, // Default placeholder
      directPlayUrl: `${url}/embed`
    };
  } catch (error) {
    console.error('Error parsing itch.io URL:', error);
    return null;
  }
};

/**
 * Fetch game metadata from itch.io page using oEmbed API
 * @param {string} url - The itch.io game URL
 * @returns {Promise<object>} - Game metadata
 */
export const fetchItchioGameData = async (url) => {
  try {
    const parsedUrl = parseItchioUrl(url);
    if (!parsedUrl) {
      throw new Error('Invalid itch.io URL');
    }

    // Try to use itch.io's oEmbed API for better data
    try {
      const oembedUrl = `https://itch.io/oembed?url=${encodeURIComponent(url)}`;
      const response = await fetch(oembedUrl);
      
      if (response.ok) {
        const data = await response.json();
        return {
          title: data.title || parsedUrl.gameName.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
          thumbnail: data.thumbnail_url || null,
          author: data.author_name || parsedUrl.username,
          originalUrl: url,
          isValid: true
        };
      }
    } catch (oembedError) {
      console.log('oEmbed failed, using fallback method');
    }
    
    // Fallback to parsed URL data
    return {
      title: parsedUrl.gameName.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
      thumbnail: null, // No thumbnail available without API
      author: parsedUrl.username,
      originalUrl: url,
      isValid: true
    };
  } catch (error) {
    console.error('Error fetching itch.io game data:', error);
    return {
      title: 'Game Title Unavailable',
      thumbnail: null,
      author: 'Unknown',
      originalUrl: url,
      isValid: false
    };
  }
};

/**
 * Generate itch.io widget HTML for embedding
 * @param {string} url - The itch.io game URL
 * @param {object} options - Widget options
 * @returns {string} - HTML for itch.io widget
 */
export const generateItchioWidget = (url, options = {}) => {
  const parsedUrl = parseItchioUrl(url);
  if (!parsedUrl) {
    return null;
  }

  const {
    width = '100%',
    height = '400',
    border = '0'
  } = options;

  return {
    embedUrl: parsedUrl.embedUrl,
    widgetHtml: `<iframe src="${parsedUrl.embedUrl}" width="${width}" height="${height}" frameborder="${border}"><a href="${url}">Play ${parsedUrl.gameName} on itch.io</a></iframe>`
  };
};

/**
 * Validate if a URL is a valid itch.io game URL
 * @param {string} url - The URL to validate
 * @returns {boolean} - Whether the URL is valid
 */
export const isValidItchioUrl = (url) => {
  if (!url || typeof url !== 'string') {
    return false;
  }

  try {
    const urlObj = new URL(url);
    const hostname = urlObj.hostname;
    const pathname = urlObj.pathname;
    
    // Check if it's an itch.io subdomain and has a game path
    return hostname.endsWith('.itch.io') && 
           hostname !== 'itch.io' && 
           pathname.length > 1 &&
           !pathname.includes('/embed');
  } catch {
    return false;
  }
};
